<template>
    <div class="marquee-wrapper" :class="`marquee-${mode}`">
        <!-- 模式1: 水平滾動文字 -->
        <div v-if="mode === 'horizontal'" class="marquee-horizontal">
            <div class="marquee-content" :style="horizontalStyle">
                <span
                    v-for="(item, index) in displayItems"
                    :key="`h-${index}`"
                    class="marquee-item"
                >
                    {{ typeof item === 'string' ? item : (item.text || item.title) }}
                </span>
            </div>
        </div>

        <!-- 模式2: 垂直輪播訊息 -->
        <div v-else-if="mode === 'vertical'" class="marquee-vertical">
            <transition :name="verticalTransition">
                <div
                    :key="currentIndex"
                    class="marquee-item"
                    @click="handleItemClick(items[currentIndex])"
                >
                    <div class="marquee-item-content" ref="contentWrapper">
                        <span v-if="showIcon" class="marquee-icon">
                            <i :class="iconClass"></i>
                        </span>
                        <div class="marquee-text-wrapper" :class="{ 'text-overflow': isTextOverflow }">
                            <span class="marquee-text" ref="textContent">
                                {{ typeof items[currentIndex] === 'string' ? items[currentIndex] : (items[currentIndex].text || items[currentIndex].title) }}
                            </span>
                            <span v-if="isTextOverflow" class="marquee-text marquee-text-clone">
                                {{ typeof items[currentIndex] === 'string' ? items[currentIndex] : (items[currentIndex].text || items[currentIndex].title) }}
                            </span>
                        </div>
                    </div>
                </div>
            </transition>
        </div>

        <!-- 模式3: 新聞快訊式 -->
        <div v-else-if="mode === 'news'" class="marquee-news">
            <div class="marquee-label" :style="{ backgroundColor: labelBgColor }">
                <i v-if="showIcon" :class="iconClass"></i>
                {{ label }}
            </div>
            <div class="marquee-news-content">
                <div class="marquee-content" :style="horizontalStyle">
                    <span
                        v-for="(item, index) in displayItems"
                        :key="`n-${index}`"
                        class="marquee-item"
                        @click="handleItemClick(item)"
                    >
                        {{ typeof item === 'string' ? item : (item.text || item.title) }}
                    </span>
                </div>
            </div>
        </div>

        <!-- 控制按鈕（可選） -->
        <div v-if="showControls && mode === 'vertical'" class="marquee-controls">
            <button class="btn-control" @click="prev" :disabled="!autoPlay && items.length <= 1">
                <i class="icon-chevron-up"></i>
            </button>
            <button class="btn-control" @click="next" :disabled="!autoPlay && items.length <= 1">
                <i class="icon-chevron-down"></i>
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Marquee',
    props: {
        // 跑馬燈模式: 'horizontal'(水平滾動), 'vertical'(垂直輪播), 'news'(新聞快訊)
        mode: {
            type: String,
            default: 'horizontal',
            validator: (value) => ['horizontal', 'vertical', 'news'].includes(value)
        },
        // 要顯示的訊息列表
        items: {
            type: Array,
            required: true,
            default: () => []
        },
        // 水平滾動速度（秒）
        speed: {
            type: Number,
            default: 20
        },
        // 垂直輪播間隔（毫秒）
        interval: {
            type: Number,
            default: 3000
        },
        // 是否自動播放
        autoPlay: {
            type: Boolean,
            default: true
        },
        // 新聞模式的標籤文字
        label: {
            type: String,
            default: '最新消息'
        },
        // 標籤背景顏色
        labelBgColor: {
            type: String,
            default: '#007bff'
        },
        // 是否顯示圖標
        showIcon: {
            type: Boolean,
            default: true
        },
        // 圖標的 class（使用 Font Awesome 或其他圖標庫）
        iconClass: {
            type: String,
            default: 'fas fa-bullhorn'
        },
        // 是否顯示控制按鈕
        showControls: {
            type: Boolean,
            default: false
        },
        // 垂直切換動畫
        verticalTransition: {
            type: String,
            default: 'slide-up',
            validator: (value) => ['slide-up', 'fade', 'slide-down'].includes(value)
        },
        // 鼠標懸停時是否暫停
        pauseOnHover: {
            type: Boolean,
            default: true
        },
        // 水平滾動時項目之間的間距
        itemGap: {
            type: String,
            default: '50px'
        }
    },
    data() {
        return {
            currentIndex: 0,
            timer: null,
            isPaused: false,
            animationDuration: this.speed,
            isTextOverflow: false,
            calculatedDuration: this.speed  // 根據實際寬度計算的動畫時間
        };
    },
    computed: {
        displayItems() {
            // 為水平滾動模式複製項目以實現無縫循環
            // 複製三次以確保最後一個項目完全顯示後才重置
            if (this.mode === 'horizontal' || this.mode === 'news') {
                return [...this.items, ...this.items, ...this.items];
            }
            return this.items;
        },
        horizontalStyle() {
            return {
                animationDuration: `${this.calculatedDuration}s`,
                animationPlayState: this.isPaused ? 'paused' : 'running',
                gap: this.itemGap
            };
        }
    },
    mounted() {
        if (this.autoPlay) {
            this.startAutoPlay();
        }

        if (this.pauseOnHover) {
            this.setupHoverListeners();
        }

        // 檢測文字是否溢出
        this.$nextTick(() => {
            this.checkTextOverflow();
            // 計算水平滾動的動畫時間
            this.calculateDurationByWidth();
        });
    },
    updated() {
        // 每次更新後重新檢測文字溢出和重新計算動畫時間
        this.$nextTick(() => {
            this.checkTextOverflow();
            this.calculateDurationByWidth();
        });
    },
    beforeDestroy() {
        this.stopAutoPlay();
    },
    methods: {
        startAutoPlay() {
            if (this.mode === 'vertical' && this.items.length > 1) {
                this.timer = setInterval(() => {
                    this.next();
                }, this.interval);
            }
        },
        stopAutoPlay() {
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }
        },
        next() {
            this.currentIndex = (this.currentIndex + 1) % this.items.length;
        },
        prev() {
            this.currentIndex = this.currentIndex === 0
                ? this.items.length - 1
                : this.currentIndex - 1;
        },
        setupHoverListeners() {
            this.$el.addEventListener('mouseenter', this.handleMouseEnter);
            this.$el.addEventListener('mouseleave', this.handleMouseLeave);
        },
        handleMouseEnter() {
            this.isPaused = true;
            if (this.mode === 'vertical') {
                this.stopAutoPlay();
            }
        },
        handleMouseLeave() {
            this.isPaused = false;
            if (this.mode === 'vertical' && this.autoPlay) {
                this.startAutoPlay();
            }
        },
        handleItemClick(item) {
            // 如果項目是物件且有 url 或 link 屬性，則進行跳轉
            if (typeof item === 'object') {
                const url = item.url || item.link;
                if (url) {
                    if (item.target === '_blank') {
                        window.open(url, '_blank');
                    } else {
                        window.location.href = url;
                    }
                }
            }
            // 觸發點擊事件給父組件
            this.$emit('item-click', item, this.currentIndex);
        },
        checkTextOverflow() {
            if (this.mode !== 'vertical') return;

            // 等待 DOM 更新
            this.$nextTick(() => {
                const wrapper = this.$refs.contentWrapper;
                const textContent = this.$refs.textContent;

                if (!wrapper || !textContent) {
                    this.isTextOverflow = false;
                    return;
                }

                // 計算可用寬度和文字寬度
                const wrapperWidth = wrapper.offsetWidth;
                const textWidth = textContent.offsetWidth;

                // 如果文字寬度超過容器寬度，啟用水平滾動
                this.isTextOverflow = textWidth > wrapperWidth;
            });
        },
        calculateDurationByWidth() {
            // 只在水平滾動模式下計算
            if (this.mode !== 'horizontal' && this.mode !== 'news') return;

            this.$nextTick(() => {
                // 獲取跑馬燈內容元素
                const contentElement = this.$el.querySelector('.marquee-content');

                if (!contentElement || this.items.length === 0) {
                    this.calculatedDuration = this.speed;
                    return;
                }

                // 獲取實際寬度（因為 displayItems 複製了三次，所以實際寬度是總寬度的三分之一）
                const totalWidth = contentElement.scrollWidth;
                const oneThirdWidth = totalWidth / 3;

                // 固定速度：50 像素/秒（可以調整這個值來改變滾動速度）
                const pixelsPerSecond = 50;

                // 計算動畫時間：寬度 ÷ 速度
                const calculatedTime = oneThirdWidth / pixelsPerSecond;

                // 設置最小時間為 10 秒，避免太快
                this.calculatedDuration = Math.max(10, calculatedTime);

                console.log(`跑馬燈寬度計算: 總寬度=${totalWidth}px, 單組寬度=${oneThirdWidth}px, 動畫時間=${this.calculatedDuration.toFixed(2)}秒`);
            });
        }
    },
    watch: {
        items() {
            // 當項目改變時重置索引
            if (this.currentIndex >= this.items.length) {
                this.currentIndex = 0;
            }
            // 重新檢測文字溢出
            this.$nextTick(() => {
                this.checkTextOverflow();
            });
        },
        currentIndex() {
            // 當索引改變時重新檢測溢出
            this.$nextTick(() => {
                this.checkTextOverflow();
            });
        },
        autoPlay(newVal) {
            if (newVal) {
                this.startAutoPlay();
            } else {
                this.stopAutoPlay();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.marquee-wrapper {
    width: 100%;
    overflow: hidden;
    position: relative;
    background-color: #17A8B2;  /* 藍綠色 */
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(62, 39, 35, 0.08);  /* 淡咖啡色陰影 */
}

/* 水平滾動模式 */
.marquee-horizontal {
    width: 100%;
    overflow: hidden;
    height: 50px;
    display: flex;
    align-items: center;

    .marquee-content {
        display: inline-flex;
        white-space: nowrap;
        animation: scroll-horizontal linear infinite;

        .marquee-item {
            padding: 0 24px;
            font-size: 16px;
            color: #FFFFFF;  /* 白色 */
            line-height: 50px;

            //&::after {
            //    content: '|';
            //    margin-left: 24px;
            //    color: #558B2F;  /* 森林綠分隔符 */
            //}

            &:last-child::after {
                content: '';
            }
        }
    }
}

@keyframes scroll-horizontal {
    0% {
        transform: translateX(0);
    }
    100% {
        transform: translateX(-33.33%);
    }
}

/* 垂直輪播模式 */
.marquee-vertical {
    position: relative;
    height: 50px;
    display: flex;
    align-items: center;
    padding: 0 16px;

    .marquee-item {
        width: 100%;
        cursor: pointer;

        .marquee-item-content {
            display: flex;
            align-items: center;
            width: 100%;

            .marquee-icon {
                margin-right: 8px;
                color: #558B2F;  /* 森林綠圖標 */
                font-size: 16px;
                flex-shrink: 0;
            }

            .marquee-text-wrapper {
                flex: 1;
                overflow: hidden;
                position: relative;
                white-space: nowrap;

                .marquee-text {
                    font-size: 14px;
                    color: #FFFFFF;  /* 白色 */
                    line-height: 50px;
                    white-space: nowrap;
                    display: inline-block;
                }

                &.text-overflow {
                    .marquee-text {
                        padding-right: 50px;
                        animation: scroll-text-horizontal 15s linear infinite;
                    }

                    .marquee-text-clone {
                        display: inline-block;
                        padding-right: 50px;
                        line-height: 50px;
                        font-size: 14px;
                        color: #FFFFFF;  /* 白色 */
                    }

                    &:hover .marquee-text,
                    &:hover .marquee-text-clone {
                        animation-play-state: paused;
                    }
                }
            }
        }

        &:hover {
            .marquee-text {
                color: #558B2F;  /* hover 時變為森林綠 */
            }
        }
    }
}

@keyframes scroll-text-horizontal {
    0% {
        transform: translateX(0);
    }
    100% {
        transform: translateX(-50%);
    }
}

/* 垂直切換動畫 */
.slide-up-enter-active, .slide-up-leave-active {
    transition: all 0.5s ease;
}
.slide-up-enter {
    transform: translateY(100%);
    opacity: 0;
}
.slide-up-leave-to {
    transform: translateY(-100%);
    opacity: 0;
}

.slide-down-enter-active, .slide-down-leave-active {
    transition: all 0.5s ease;
}
.slide-down-enter {
    transform: translateY(-100%);
    opacity: 0;
}
.slide-down-leave-to {
    transform: translateY(100%);
    opacity: 0;
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 0.5s ease;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}

/* 新聞快訊模式 */
.marquee-news {
    display: flex;
    align-items: center;

    .marquee-label {
        flex-shrink: 0;
        padding: 12px 16px;
        color: #fff;
        font-weight: bold;
        font-size: 14px;
        display: flex;
        align-items: center;
        gap: 6px;

        i {
            font-size: 16px;
        }
    }

    .marquee-news-content {
        flex: 1;
        overflow: hidden;

        .marquee-content {
            display: inline-flex;
            white-space: nowrap;
            animation: scroll-horizontal linear infinite;
            padding: 12px 0;

            .marquee-item {
                padding: 0 24px;
                font-size: 16px;
                color: #FFFFFF;  /* 白色 */
                cursor: pointer;

                &:hover {
                    color: #558B2F;  /* hover 時變為森林綠 */
                }

                //&::after {
                //    content: '|';
                //    margin-left: 24px;
                //    color: #558B2F;  /* 森林綠分隔符 */
                //}

                &:last-child::after {
                    content: '';
                }
            }
        }
    }
}

/* 控制按鈕 */
.marquee-controls {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    gap: 4px;

    .btn-control {
        width: 24px;
        height: 24px;
        border: 1px solid #ddd;
        background: #fff;
        border-radius: 2px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s;

        &:hover:not(:disabled) {
            background: #007bff;
            border-color: #007bff;
            color: #fff;
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        i {
            font-size: 12px;
        }
    }
}

/* 響應式設計 */
@media (max-width: 768px) {
    .marquee-horizontal .marquee-content .marquee-item,
    .marquee-news .marquee-news-content .marquee-content .marquee-item {
        font-size: 16px;
        padding: 0 16px;

        &::after {
            margin-left: 16px;
        }
    }

    .marquee-vertical .marquee-item .marquee-item-content .marquee-text {
        font-size: 13px;
    }

    .marquee-news .marquee-label {
        padding: 10px 12px;
        font-size: 13px;
    }
}
</style>
